import React, { useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import { useDarkMode } from "../../hooks/useDarkMode";
import ScrollingBanner from "./ScrollingBanner";
import { Helmet } from "react-helmet";

/* ================= FAQ DATA ================= */
const seoFaqs = [
  {
    id: 1,
    question: "What is Seo Up Rank Tool and how does it work?",
    answer:
      "Seo Up Rank Tool is an AI powered WordPress SEO automation platform that analyzes more than 50 SEO metrics and automatically fixes SEO issues with a single click.",
  },
  {
    id: 2,
    question: "How is Seo Up Rank Tool different from Yoast or RankMath?",
    answer:
      "Unlike Yoast or RankMath which only provide recommendations, Seo Up Rank Tool automatically applies AI powered SEO improvements directly to your website.",
  },
  {
    id: 3,
    question: "Is Seo Up Rank Tool safe for live WordPress websites?",
    answer:
      "Yes. Seo Up Rank Tool includes backup and rollback features so you can safely apply SEO changes and restore previous versions anytime.",
  },
  {
    id: 4,
    question: "Who should use Seo Up Rank Tool?",
    answer:
      "Seo Up Rank Tool is perfect for bloggers, startups, digital marketers, agencies and small businesses that want powerful SEO results without technical knowledge.",
  },
];

const FAQSection = () => {
  const [openIndex, setOpenIndex] = useState(null);
  const { isDarkMode } = useDarkMode();

  /* ================= THEME ================= */
  const pageBg = isDarkMode ? "bg-gray-950" : "bg-white";
  const cardBg = isDarkMode
    ? "bg-gray-900 border-green-500/40"
    : "bg-white border-green-600/40";

  const questionText = isDarkMode ? "text-white" : "text-gray-800";
  const answerText = isDarkMode ? "text-gray-300" : "text-gray-600";
  const circleColor = isDarkMode ? "bg-green-500/10" : "bg-[#008300]/10";

  /* ================= FAQ SCHEMA ================= */
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: seoFaqs.map((faq) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.answer,
      },
    })),
  };

  return (
    <>
      <Helmet>
        {/* ===== PRIMARY SEO ===== */}
        <title>
          SEO Tool FAQs | Seo Up Rank AI SEO Automation Platform Questions
        </title>

        <meta
          name="description"
          content="Find answers to common SEO questions about AI powered SEO tools, website optimization, keyword research, and ranking improvements with Seo Up Rank."
        />

        <meta
          name="keywords"
          content="SEO FAQs, SEO questions, AI SEO tools, WordPress SEO automation, SEO ranking improvement, keyword research FAQ, Seo Up Rank"
        />

        <meta name="author" content="Seo Up Rank" />
        <meta name="robots" content="index, follow, max-image-preview:large" />

        {/* ===== CANONICAL ===== */}
        <link rel="canonical" href="https://novarsis.ai/faqs" />

        {/* ===== OPEN GRAPH ===== */}
        <meta property="og:title" content="SEO Tool FAQs - Seo Up Rank" />
        <meta
          property="og:description"
          content="Frequently asked questions about AI powered SEO automation tools and WordPress SEO optimization."
        />
        <meta property="og:url" content="https://novarsis.ai/faqs" />
        <meta property="og:type" content="website" />
        <meta
          property="og:image"
          content="https://novarsis.ai/seo-banner.png"
        />
        <meta property="og:site_name" content="Seo Up Rank" />

        {/* ===== TWITTER SEO ===== */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="SEO Tool FAQs - Seo Up Rank" />
        <meta
          name="twitter:description"
          content="Common SEO questions answered about optimization, keyword research and ranking strategies."
        />
        <meta
          name="twitter:image"
          content="https://novarsis.ai/seo-banner.png"
        />

        {/* ===== FAQ SCHEMA ===== */}
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>

        {/* ===== WEBPAGE SCHEMA ===== */}
        <script type="application/ld+json">
          {`
          {
            "@context":"https://schema.org",
            "@type":"WebPage",
            "name":"SEO FAQs",
            "url":"https://novarsis.ai/faqs",
            "description":"Frequently asked questions about Seo Up Rank AI SEO platform."
          }
          `}
        </script>

        {/* ===== BREADCRUMB SCHEMA ===== */}
        <script type="application/ld+json">
          {`
          {
            "@context":"https://schema.org",
            "@type":"BreadcrumbList",
            "itemListElement":[
              {
                "@type":"ListItem",
                "position":1,
                "name":"Home",
                "item":"https://novarsis.ai"
              },
              {
                "@type":"ListItem",
                "position":2,
                "name":"FAQs",
                "item":"https://novarsis.ai/faqs"
              }
            ]
          }
          `}
        </script>
      </Helmet>

      {/* ================= HERO ================= */}
      <section className="relative h-[60vh] flex items-center justify-center overflow-hidden ">
        <video
          className="absolute inset-0 w-full h-full object-cover"
          src="/Images/FAQS-VIDEO.mp4"
          autoPlay
          loop
          muted
          playsInline
          poster="/Images/FAQS-BANNER.png"
        />

        <div className="absolute inset-0 bg-black/60"></div>

        <div className="relative z-10 text-center px-6 max-w-4xl">
          <h1 className="text-4xl md:text-6xl font-bold text-white">
            SEO <span className="text-[#008300]">Tool</span> FAQs
          </h1>

          <p className="mt-4 text-lg md:text-xl text-gray-200">
            Answers to common questions about our AI powered WordPress SEO
            automation platform.
          </p>
        </div>
      </section>

      {/* ================= FAQ SECTION ================= */}
      <section
        aria-label="Frequently asked questions about Seo Up Rank AI SEO tool"
        className={`relative overflow-hidden py-20 ${pageBg}`}
      >
        {/* Decorative Circles */}
        <div
          aria-hidden="true"
          className={`absolute -top-20 -left-20 w-40 h-40 ${circleColor} rounded-full`}
        />
        <div
          aria-hidden="true"
          className={`absolute top-48 -right-20 w-36 h-36 ${circleColor} rounded-full`}
        />
        <div
          aria-hidden="true"
          className={`absolute bottom-24 left-24 w-28 h-28 ${circleColor} rounded-full`}
        />

        <div className="relative z-10 max-w-4xl mx-auto px-4">
          {/* ================= HEADER ================= */}
          <header className="text-center mb-12">
            <h2 className={`text-3xl md:text-4xl font-bold ${questionText}`}>
              Frequently Asked <span className="text-[#008300]">Questions</span>
            </h2>

            <p className={`mt-3 ${answerText}`}>
              Learn how the <strong>Seo Up Rank AI SEO tool</strong> helps
              improve website rankings, automate WordPress SEO optimization, and
              increase organic traffic through smart SEO automation.
            </p>
          </header>

          {/* ================= FAQ LIST ================= */}
          <div
            className="space-y-4"
            role="list"
            aria-label="SEO tool frequently asked questions"
          >
            {seoFaqs.map((faq, index) => {
              const isOpen = openIndex === index;

              return (
                <article
                  key={faq.id}
                  role="listitem"
                  className={`border rounded-xl p-5 ${cardBg}`}
                >
                  {/* Question */}
                  <button
                    onClick={() => setOpenIndex(isOpen ? null : index)}
                    className="w-full flex justify-between items-center text-left"
                    aria-expanded={isOpen}
                    aria-controls={`faq-${faq.id}`}
                  >
                    <h3
                      className={`text-base md:text-lg font-semibold ${questionText}`}
                    >
                      {faq.question}
                    </h3>

                    {isOpen ? (
                      <ChevronUp
                        aria-hidden="true"
                        className="text-[#008300]"
                      />
                    ) : (
                      <ChevronDown
                        aria-hidden="true"
                        className="text-[#008300]"
                      />
                    )}
                  </button>

                  {/* Answer */}
                  {isOpen && (
                    <p
                      id={`faq-${faq.id}`}
                      className={`mt-3 text-sm md:text-base ${answerText}`}
                    >
                      {faq.answer}
                    </p>
                  )}
                </article>
              );
            })}
          </div>

          {/* ================= INTERNAL LINK ================= */}
          <div className="sr-only">
            <div className="text-center mt-12">
              <a
                href="/pricing"
                className="text-[#008300] font-semibold hover:underline"
                aria-label="View Seo Up Rank pricing plans"
              >
                Explore Seo Up Rank Pricing →
              </a>
            </div>
          </div>
        </div>
      </section>

      <ScrollingBanner />
    </>
  );
};

export default FAQSection;
